/* global window */

import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type Theme, useTheme } from '@dr.pogodin/react-themes';

import Tooltip, {
  PLACEMENTS,
  type ThemeKeysT as TooltipThemeKeysT,
} from './Tooltip';

import defaultTheme from './default-theme.scss';

type PropsT = {
  children?: ReactNode;
  placement?: PLACEMENTS;
  theme?: Theme<'wrapper' | TooltipThemeKeysT>;
  tip?: ReactNode;
};

type TooltipRefT = {
  pointTo: (
    x: number,
    y: number,
    placement: PLACEMENTS,
    wrapperRef: HTMLDivElement,
  ) => void;
};

type HeapT = {
  lastCursorX: number;
  lastCursorY: number;
  timerId?: NodeJS.Timeout;
  triggeredByTouch: boolean;
};

/**
 * Implements a simple to use and themeable tooltip component, _e.g._
 * ```js
 * <WithTooltip tip="This is example tooltip.">
 *   <p>Hover to see the tooltip.</p>
 * </WithTooltip>
 * ```
 * **Children:** Children are rendered in the place of `<WithTooltip>`,
 * and when hovered the tooltip is shown. By default the wrapper itself is
 * `<div>` block with `display: inline-block`.
 * @param tip &ndash; Anything React is able to render,
 * _e.g._ a tooltip text. This will be the tooltip content.
 * @param {WithTooltipTheme} props.theme _Ad hoc_ theme.
 */
const WithTooltip: FunctionComponent<PropsT> = ({
  children,
  placement = PLACEMENTS.ABOVE_CURSOR,
  theme,
  tip,
}) => {
  const custom = useTheme('WithTooltip', defaultTheme, theme);

  const { current: heap } = useRef<HeapT>({
    lastCursorX: 0,
    lastCursorY: 0,
    timerId: undefined,
    triggeredByTouch: false,
  });
  const tooltipRef = useRef<TooltipRefT>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const updatePortalPosition = (cursorX: number, cursorY: number) => {
    if (showTooltip) {
      const wrapperRect = wrapperRef.current!.getBoundingClientRect();
      if (
        cursorX < wrapperRect.left
        || cursorX > wrapperRect.right
        || cursorY < wrapperRect.top
        || cursorY > wrapperRect.bottom
      ) {
        setShowTooltip(false);
      } else if (tooltipRef.current) {
        tooltipRef.current.pointTo(
          cursorX + window.scrollX,
          cursorY + window.scrollY,
          placement,
          wrapperRef.current!,
        );
      }
    } else {
      heap.lastCursorX = cursorX;
      heap.lastCursorY = cursorY;

      // If tooltip was triggered by a touch, we delay its opening by a bit,
      // to ensure it was not a touch-click - in the case of touch click we
      // want to do the click, rather than show the tooltip, and the delay
      // gives click handler a chance to abort the tooltip openning.
      if (heap.triggeredByTouch) {
        heap.timerId ??= setTimeout(() => {
          heap.triggeredByTouch = false;
          heap.timerId = undefined;
          setShowTooltip(true);
        }, 300);

      // Otherwise we can just open the tooltip right away.
      } else setShowTooltip(true);
    }
  };

  useEffect(() => {
    if (showTooltip && tip !== null) {
      // This is necessary to ensure that even when a single mouse event
      // arrives to a tool-tipped component, the tooltip is correctly positioned
      // once opened (because similar call above does not have effect until
      // the tooltip is fully mounted, and that is delayed to future rendering
      // cycle due to the implementation).
      if (tooltipRef.current) {
        tooltipRef.current.pointTo(
          heap.lastCursorX + window.scrollX,
          heap.lastCursorY + window.scrollY,
          placement,
          wrapperRef.current!,
        );
      }

      const listener = () => {
        setShowTooltip(false);
      };
      window.addEventListener('scroll', listener);
      return () => {
        window.removeEventListener('scroll', listener);
      };
    }
    return undefined;
  }, [
    heap.lastCursorX,
    heap.lastCursorY,
    placement,
    showTooltip,
    tip,
  ]);

  return (
    <div
      className={custom.wrapper}
      onClick={() => {
        if (heap.timerId) {
          clearTimeout(heap.timerId);
          heap.timerId = undefined;
          heap.triggeredByTouch = false;
        }
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
      }}
      onMouseMove={(e) => {
        updatePortalPosition(e.clientX, e.clientY);
      }}
      onTouchStart={() => {
        heap.triggeredByTouch = true;
      }}
      ref={wrapperRef}
      role="presentation"
    >
      {
        showTooltip && tip !== null
          ? <Tooltip ref={tooltipRef} theme={custom}>{tip}</Tooltip>
          : null
      }
      {children}
    </div>
  );
};

type ExportT = {
  PLACEMENTS: typeof PLACEMENTS;
} & typeof WithTooltip;

const e: ExportT = WithTooltip as ExportT;

e.PLACEMENTS = PLACEMENTS;

export default e;
